// Google Identity Services (GIS) helper.
//
// Problem this solves
// -------------------
// The auth pages (LoginPage, RegisterPage) each used to call
// `window.google.accounts.id.initialize(...)` on mount (and RegisterPage
// re-ran it on every `role` toggle via a useEffect dep). GIS is a
// singleton on the window — it keeps global state — and warns:
//
//   [GSI_LOGGER]: google.accounts.id.initialize() is called multiple
//   times. This could cause unexpected behavior and only the last
//   initialized instance will be used.
//
// Worse: only the LAST init's callback survives, so a user toggling
// Register from Salon → Client could have their Google sign-in fire a
// stale role-specific handler after the callback was replaced.
//
// Fix
// ---
// Module-scope guard (`initialized`) ensures `.initialize()` runs
// exactly once per app lifetime, regardless of how many times the
// helper is called. The callback is a stable wrapper that reads from
// a ref-like holder (`latestCallback`) updated on every call — so the
// currently-mounted component's closure (with fresh `role`, fresh
// `loginWithGoogle`, etc.) always wins.
//
// Button rendering is NOT gated by this — every mount can freely
// render a button into its own DOM node. Only the one-time
// initialization is coalesced.

import { isNativeApp } from './platform';

let initialized = false;
let latestCallback = null;

/**
 * Ensure GIS is initialized exactly once, pointing at `callback`.
 * Subsequent calls replace the callback pointer without re-calling
 * `initialize()`. Returns true if GIS is ready to use right now,
 * false if the `google.accounts.id` namespace isn't loaded yet
 * (caller should poll and retry — the GIS script loads async).
 */
export function ensureGoogleInitialized(clientId, callback) {
  // Native (Capacitor iOS): Google blocks OAuth in a WKWebView
  // (disallowed_useragent), so don't load GIS or spin the init poller — the
  // sign-in button is hidden on the auth pages there. Native Google auth is a
  // separate follow-up (Capacitor Browser / ASWebAuthenticationSession).
  if (isNativeApp) return false;
  latestCallback = callback;
  if (!window.google?.accounts?.id) return false;
  if (initialized) return true;
  window.google.accounts.id.initialize({
    client_id: clientId,
    // Stable wrapper — reads the current callback at invocation time.
    // Without this indirection GIS would be locked to the first
    // callback we ever passed and newer mounts' handlers would
    // silently do nothing.
    callback: async (response) => {
      // `await` so async rejections from the registered callback
      // (the auth pages' handlers are async and call loginWithGoogle)
      // route through this catch instead of becoming an
      // UnhandledPromiseRejection. The current handlers self-catch,
      // but the wrapper shouldn't depend on that contract — a future
      // caller that forgets the inner try/catch would otherwise lose
      // the error to GIS's silent swallow.
      try {
        await latestCallback?.(response);
      } catch (err) {
        console.error('[googleSignIn] callback threw:', err);
      }
    },
    auto_select: false,
  });
  initialized = true;
  return true;
}

/**
 * Render the Google button into `container`. Safe to call repeatedly
 * — GIS itself handles DOM idempotency, but we still clear the node
 * first so a previous render's stale button doesn't sit alongside.
 */
export function renderGoogleButton(container, options) {
  if (!window.google?.accounts?.id || !container) return;
  container.innerHTML = '';
  // GIS only honours a button width in the [250, 400] px range. Pass MORE and it
  // ignores the value and renders at its natural width — which, in a full-width
  // container, stranded the logo far-left and centred the label, leaving a big gap
  // (the "broken bar"). Pass LESS (a narrow container, or offsetWidth read as a
  // tiny value mid-mount / during the drawer's slide-in transition) and GIS can
  // render glitched. Clamp into [250, 400]. shape:'pill' matches the site's
  // rounded-full controls; logo_alignment:'center' keeps the logo + label snug.
  const opts = { shape: 'pill', logo_alignment: 'center', ...options };
  if (opts.width) opts.width = Math.max(250, Math.min(Math.round(opts.width), 400));
  window.google.accounts.id.renderButton(container, opts);
}
